import { QuaternionTuple, Vector3, Web3DViewer } from '@technology/web3d';
import {
  FrustumCapability,
  NavigationalCapabilityBase,
  StationsNavigationCapability,
  findClosestStation,
} from '@trimble/web3d-capabilities';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import {
  Point2D,
  Point3D,
  arePointsEqual,
  partialPointToVector3,
} from '@trimble/utilities';
import {
  CapabilityRegistryContainer,
  NavigationCapabilityCoordinator,
} from '@trimble/web3d';

export function getActiveNavCap(
  pane: CapabilityRegistryContainer
): NavigationalCapabilityBase | undefined {
  return pane
    .get(NavigationCapabilityCoordinator)()
    ?.activeNavigationCapability();
}

const NDC_TO_SCREEN_DIVISOR = 2;

export function isPointVisibleInMap(
  point: Vector3,
  viewer: Web3DViewer
): boolean {
  const matrixWorldInverse = viewer.camera.matrixWorld.clone().invert();
  const projectionMatrix = viewer.camera.projectionMatrix.clone();
  const mapPoint = new Vector3(point.x, point.y, 0);

  const pos = mapPoint.clone();
  pos.applyMatrix4(matrixWorldInverse);
  pos.applyMatrix4(projectionMatrix);

  const screenX = ((pos.x + 1) * viewer.clientWidth) / NDC_TO_SCREEN_DIVISOR;
  const screenY = ((pos.y + 1) * viewer.clientHeight) / NDC_TO_SCREEN_DIVISOR;

  return !(
    screenX < 0 ||
    screenX > viewer.clientWidth ||
    screenY < 0 ||
    screenY > viewer.clientHeight
  );
}

export function updateFrustum(
  targetPane: CapabilityRegistryContainer,
  targetPosition: Point3D | Point2D | Vector3 | null,
  quaternion: QuaternionTuple | null,
  fov: number | null
): void {
  const frustumCap = targetPane.get(FrustumCapability)();
  frustumCap?.setFrustumPosition({ targetPosition, quaternion, fov });
}

/**
 * Finds the closest station to `syncPosition` in the target pane and
 * navigates to it, skipping the navigation when the camera is already there.
 * Shared between station→station and alignment→station sync.
 */
export function navigateToClosestStation(
  syncPosition: Vector3,
  targetNavCap: StationsNavigationCapability,
  stationsProvider: StationsCapabilityProvider,
  targetViewer: { camera: { position: Vector3 } }
): void {
  const closestStationInfo = findClosestStation(
    partialPointToVector3(syncPosition),
    stationsProvider.stationsInfo().map((s) => s.position)
  );
  if (
    closestStationInfo &&
    !arePointsEqual(targetViewer.camera.position, closestStationInfo.position)
  ) {
    targetNavCap.goToStation(closestStationInfo.index);
  }
}
